import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Coffee, LogOut, MapPin, Package, Plus, Settings2, Timer, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Skeleton } from '@/components/ui/Skeleton';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { WorkerChip, QuietEmpty, StoredImage } from './shared';
import { useCreateWorkstation } from '@/features/inventory/hooks';
import { useCloseStaleSessions, useEndSession, useOpenBreaks, useOpenSessions, useRecentUnits } from '@/features/workMode/hooks';
import { describeWorkError, formatClock, formatDuration, secondsSince } from '@/features/workMode/api';
import { stationHref } from '@/features/workMode/links';
import { efficiency, paceTone, type PaceTone } from '@/features/workMode/gamification';
import { BREAK_REASON_LABEL } from '@/features/workMode/labels';
import { useNow } from '@/features/workMode/kiosk';
import type { WorkLookups } from '@/features/workMode/useLookups';
import type { FloorWorkstation, WorkBreak, WorkSession } from '@/features/workMode/types';

interface LiveStationsTabProps {
  lookups: WorkLookups;
  canManage: boolean;
}

const PACE_CLASS: Record<PaceTone, string> = {
  ahead: 'text-bright-moss-dark',
  on: 'text-foreground',
  behind: 'text-fresh-salmon-dark',
  none: 'text-muted-foreground',
};

function startOfToday(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * One card per station: who is there, what they are building, how long the
 * current unit has been going and how today is adding up. Configuration
 * (photos, tools, parts, instructions, notes) lives on the station page.
 */
export function LiveStationsTab({ lookups, canManage }: LiveStationsTabProps) {
  const navigate = useNavigate();
  const { data: sessions = [], isLoading: sessionsLoading } = useOpenSessions();
  const { data: breaks = [] } = useOpenBreaks();
  const { data: recentUnits = [] } = useRecentUnits(500);
  const closeStale = useCloseStaleSessions();
  const [creating, setCreating] = useState(false);

  const sessionByStation = useMemo(() => new Map(sessions.map((s) => [s.workstation_id, s])), [sessions]);
  const breakBySession = useMemo(() => new Map(breaks.map((b) => [b.session_id, b])), [breaks]);

  const todayByStation = useMemo(() => {
    const since = startOfToday();
    const map = new Map<string, { good: number; scrap: number; durations: number[] }>();
    for (const u of recentUnits) {
      if (!u.workstation_id || Date.parse(u.completed_at) < since) continue;
      const cur = map.get(u.workstation_id) ?? { good: 0, scrap: 0, durations: [] };
      if (u.outcome === 'good') {
        cur.good += 1;
        cur.durations.push(u.duration_seconds - u.break_seconds);
      } else cur.scrap += 1;
      map.set(u.workstation_id, cur);
    }
    return map;
  }, [recentUnits]);

  const stations = lookups.stationList;
  const active = stations.filter((s) => s.is_active);
  const inactive = stations.filter((s) => !s.is_active);
  const working = active.filter((s) => sessionByStation.has(s.id)).length;

  if (lookups.loading || sessionsLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-56 rounded-2xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{working}</span> of {active.length} station{active.length === 1 ? '' : 's'} working ·{' '}
          {sessions.length} open shift{sessions.length === 1 ? '' : 's'}
        </p>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/digital-company">
              <MapPin className="h-4 w-4" />
              See it on Digital Company
            </Link>
          </Button>
          {canManage && (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={closeStale.isPending}
                onClick={() =>
                  closeStale.mutate(
                    { hours: 12 },
                    {
                      onSuccess: (n) => toast.success(n ? `Closed ${n} stale shift${n === 1 ? '' : 's'}.` : 'Nothing stale to close.'),
                      onError: (e) => toast.error(describeWorkError(e)),
                    },
                  )
                }
              >
                <TriangleAlert className="h-4 w-4" />
                Close stale shifts
              </Button>
              <Button size="sm" onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" />
                New station
              </Button>
            </>
          )}
        </div>
      </div>

      {active.length === 0 ? (
        <QuietEmpty>No active workstations yet.</QuietEmpty>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {active.map((s) => {
            const session = sessionByStation.get(s.id) ?? null;
            return (
              <StationCard
                key={s.id}
                station={s}
                session={session}
                openBreak={session ? (breakBySession.get(session.id) ?? null) : null}
                today={todayByStation.get(s.id)}
                lookups={lookups}
                canManage={canManage}
              />
            );
          })}
        </div>
      )}

      {inactive.length > 0 && (
        <div>
          <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Switched off</p>
          <ul className="flex flex-wrap gap-2">
            {inactive.map((s) => (
              <li key={s.id}>
                <Link to={stationHref(s.id)} className="inline-block rounded-full border border-dashed border-border px-3 py-1 text-sm text-muted-foreground hover:bg-secondary">
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {creating && (
        <NewStationDialog
          existing={stations}
          onOpenChange={(o) => !o && setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            navigate(stationHref(id));
          }}
        />
      )}
    </div>
  );
}

/** Name a new workstation. Mounted only while open so the field starts blank each time. */
function NewStationDialog({
  existing,
  onOpenChange,
  onCreated,
}: {
  existing: FloorWorkstation[];
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const create = useCreateWorkstation();
  const [name, setName] = useState('');
  const trimmed = name.trim();
  const duplicate = existing.find((s) => s.name.toLowerCase() === trimmed.toLowerCase()) ?? null;

  const submit = () => {
    if (!trimmed || duplicate) return;
    create.mutate(
      { name: trimmed },
      {
        onSuccess: (row) => {
          toast.success(`Added ${row.name}.`);
          onCreated(row.id);
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>New workstation</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="new-station-name">Name</Label>
            <Input id="new-station-name" value={name} maxLength={64} autoFocus onChange={(e) => setName(e.target.value)} placeholder="Station 2 — Bases" />
            <p className="min-h-4 text-xs text-muted-foreground">
              {duplicate ? `“${duplicate.name}” already exists${duplicate.is_active ? '' : ' (switched off — turn it back on instead)'}.` : ''}
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!trimmed || !!duplicate || create.isPending}>
              Add station
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StationCard({
  station,
  session,
  openBreak,
  today,
  lookups,
  canManage,
}: {
  station: FloorWorkstation;
  session: WorkSession | null;
  openBreak: WorkBreak | null;
  today: { good: number; scrap: number; durations: number[] } | undefined;
  lookups: WorkLookups;
  canManage: boolean;
}) {
  const now = useNow(1000);
  const end = useEndSession();
  const worker = session ? lookups.person(session.user_id) : null;
  const itemId = session?.item_id ?? station.default_item_id;
  const itemName = lookups.itemName(itemId);
  const unitSeconds = session ? secondsSince(session.unit_started_at, now) - (openBreak ? secondsSince(openBreak.started_at, now) : 0) : 0;
  const avgToday = today && today.durations.length ? today.durations.reduce((a, b) => a + b, 0) / today.durations.length : null;
  const target = station.target_seconds;
  const eff = efficiency(avgToday, target);
  const tone = paceTone(eff);
  const overTarget = !!target && !openBreak && unitSeconds > target * 1.25;

  return (
    <article
      className={cn(
        'flex flex-col rounded-2xl border bg-card p-4 transition-colors',
        session ? (openBreak ? 'border-atmosphere' : 'border-bright-moss') : 'border-border',
      )}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {station.cover_image_path && <StoredImage bucket="work-instructions" path={station.cover_image_path} alt="" className="h-10 w-14 shrink-0" />}
          <div className="min-w-0">
            <h3 className="truncate text-base font-medium text-foreground">
              <Link to={stationHref(station.id)} className="hover:underline">
                {station.name}
              </Link>
            </h3>
            <p className="truncate text-xs text-muted-foreground">{station.description || (itemName ? `Builds ${itemName}` : 'No default item')}</p>
          </div>
        </div>
        <Badge variant={session ? (openBreak ? 'info-soft' : 'success-soft') : 'muted'}>
          {session ? (openBreak ? 'On break' : 'Working') : 'Empty'}
        </Badge>
      </header>

      <div className="mt-4 flex-1">
        {session && worker ? (
          <div className="space-y-4">
            <WorkerChip person={worker} sub={itemName ? `Building ${itemName}` : 'No item picked'} size={40} />
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Current unit</p>
                <p className={cn('text-2xl font-medium tabular-nums', overTarget ? 'text-fresh-salmon-dark' : 'text-foreground')}>
                  {openBreak ? <Coffee className="inline h-6 w-6 text-atmosphere-dark" /> : formatClock(unitSeconds)}
                </p>
                {openBreak && <p className="text-xs text-muted-foreground">{BREAK_REASON_LABEL[openBreak.reason] ?? 'Paused'}</p>}
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">This shift</p>
                <p className="text-2xl font-medium tabular-nums text-foreground">{session.units_completed}</p>
                {session.units_scrapped > 0 && <p className="text-xs text-fresh-salmon-dark">{session.units_scrapped} scrapped</p>}
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Since</p>
                <p className="text-2xl font-medium tabular-nums text-foreground">
                  {new Date(session.started_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full min-h-[96px] flex-col items-center justify-center gap-1 rounded-xl bg-secondary/50 text-center">
            <Package className="h-5 w-5 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Nobody signed in</p>
          </div>
        )}
      </div>

      <footer className="mt-4 flex items-end justify-between gap-3 border-t border-border pt-3">
        <div className="text-xs text-muted-foreground">
          <p>
            Today: <span className="font-medium text-foreground">{today?.good ?? 0}</span> done
            {today?.scrap ? <span className="text-fresh-salmon-dark"> · {today.scrap} scrap</span> : null}
          </p>
          <p className={cn('mt-0.5 flex items-center gap-1', PACE_CLASS[tone])}>
            <Timer className="h-3 w-3" />
            {avgToday ? `avg ${formatDuration(avgToday)}` : 'no cycles yet'}
            {target ? ` · target ${formatDuration(target)}` : ''}
            {eff !== null ? ` · ${Math.round(eff * 100)}%` : ''}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {canManage && session && (
            <Button
              variant="ghost"
              size="sm"
              disabled={end.isPending}
              onClick={() => {
                if (!window.confirm(`End ${worker?.name ?? 'this'} shift at ${station.name}?`)) return;
                end.mutate(
                  { sessionId: session.id, reason: 'manager' },
                  {
                    onSuccess: () => toast.success('Shift ended.'),
                    onError: (e) => toast.error(describeWorkError(e)),
                  },
                );
              }}
            >
              <LogOut className="h-4 w-4" />
              End shift
            </Button>
          )}
          <Button asChild variant="ghost" size="icon" aria-label={`${canManage ? 'Set up' : 'Open'} ${station.name}`}>
            <Link to={stationHref(station.id)}>
              <Settings2 className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </footer>
    </article>
  );
}
